import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../components/AuthContext';
import HeaderLink from '../components/ui/HeaderLink';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Box } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useBasket } from './BasketContext';
import { navigationLinks } from './ui/data/navigationLinks';
import { useMenu } from './MenuContext';
import { useTranslation } from 'react-i18next';

const FALLBACK_MENU_ITEMS = [
    { id: 'fallback-home', name: 'Home', url_path: '/', item_type: 'link' },
    { id: 'fallback-shop', name: 'Our Tees', url_path: '/shop/', item_type: 'link' },
    { id: 'fallback-about', name: 'About', url_path: '/about/', item_type: 'link' },
    { id: 'fallback-faq', name: 'FAQ', url_path: '/support/faq/', item_type: 'link' },
    { id: 'fallback-contact', name: 'Contact', url_path: '/contact/', item_type: 'link' },
];

const DROP_STUDIO_MENU_ITEM = {
    id: 'drop-studio',
    name: 'Studio',
    item_type: 'scroll',
    url_path: '/#merch-playground',
};

const OPEN_MERCH_PLAYGROUND_EVENT = 'thatshirtshow:open-merch-playground';
const CLOSE_MERCH_PLAYGROUND_EVENT = 'thatshirtshow:close-merch-playground';

const insertDropStudioAfterHome = (items = []) => {
    const homeIndex = items.findIndex((item) => {
        const path = item.url_path || '';
        return path === '/' || path === '';
    });
    if (homeIndex === -1) return items;
    const next = [...items];
    next.splice(homeIndex + 1, 0, DROP_STUDIO_MENU_ITEM);
    return next;
};

const openMerchPlayground = () => {
    window.dispatchEvent(new CustomEvent(OPEN_MERCH_PLAYGROUND_EVENT));
    return true;
};

export default function Header() {
    const { isAuthorized } = useAuth();
    const [showMobilePanel, setShowMobilePanel] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { basketCount, isUpdating } = useBasket();
    const { menuItems, isLoading } = useMenu();
    const [activeDropdown, setActiveDropdown] = useState(null);
    const { t } = useTranslation();
    const displayedMenuItems = menuItems.length > 0 ? menuItems : FALLBACK_MENU_ITEMS;
    const navItemsWithStudio = insertDropStudioAfterHome(displayedMenuItems);
    const desktopNavItems = navItemsWithStudio;
    const headerRef = useRef(null);
    const [isMerchPlaygroundOpen, setIsMerchPlaygroundOpen] = useState(false);

    const toggleMobilePanel = () => {
        setShowMobilePanel(!showMobilePanel);
    };

    const scrollToTop = () => {
        window.scrollTo(0, 0);
    };

    const navigateToTop = (url, options) => {
        scrollToTop();
        navigate(url, options);
        requestAnimationFrame(scrollToTop);
    };

    const handleMobileLinkClick = (url) => {
        scrollToTop();
        setShowMobilePanel(false);
        navigateToTop(url);
    };

    const normalizePath = (path = '') => {
        if (!path || path === '/') return '/';
        return path.endsWith('/') ? path.slice(0, -1) : path;
    };

    const resolveMenuUrl = (url = '') => {
        return normalizePath(url) === '/shop/custom-request' ? '/custom-request' : url;
    };

    const isDropStudioTarget = (item = {}) => {
        const targetUrl = resolveMenuUrl(item.url_path || '');
        const itemName = String(item.name || '').trim().toLowerCase();
        return item.id === DROP_STUDIO_MENU_ITEM.id
            || itemName === 'studio'
            || itemName === 'drop studio'
            || item.item_type === 'scroll'
            || targetUrl === '/#merch-playground'
            || targetUrl === '#merch-playground';
    };

    const isActivePath = (path) => {
        return normalizePath(location.pathname).startsWith(normalizePath(path));
    };

    const handleDropStudioClick = () => {
        openMerchPlayground();
    };

    const handleMenuItemClick = (item) => {
        if (item.item_type === 'dropdown') {
            return;
        }

        if (isDropStudioTarget(item)) {
            handleDropStudioClick();
            if (showMobilePanel) {
                toggleMobilePanel();
            }
            return;
        }
        
        const targetUrl = resolveMenuUrl(item.url_path);

        if (targetUrl.startsWith('/category/')) {
            const categoryId = targetUrl.split('/').pop();
            navigateToTop('/');
            window.dispatchEvent(new CustomEvent('filterByCategory', { 
                detail: { categoryId } 
            }));
        } else {
            navigateToTop(targetUrl);
        }
        
        if (showMobilePanel) {
            toggleMobilePanel();
        }
    };

    const handleAccountClick = () => {
        if (isAuthorized) {
            navigateToTop('/account/profile');
        } else {
            navigateToTop('/login');
        }
    };

    const isCataloguePath = (path = '') => normalizePath(path).startsWith('/catalogue/');

    const isShopCataloguePath = (path = '') => {
        const normalizedPath = normalizePath(path);
        return normalizedPath === '/shop' || isCataloguePath(normalizedPath);
    };

    const isActiveNavPath = (path = '') => {
        const currentLocation = normalizePath(location.pathname);
        const targetPath = normalizePath(resolveMenuUrl(path));

        if (targetPath === '/') {
            return currentLocation === '/';
        }
        if (isShopCataloguePath(targetPath)) {
            return isShopCataloguePath(currentLocation);
        }
        if (targetPath === '/shop/orders') {
            return currentLocation.startsWith('/shop/orders')
                || currentLocation.startsWith('/shop/checkout/gateway');
        }
        return currentLocation === targetPath || currentLocation.startsWith(`${targetPath}/`);
    };

    const isActiveSubMenuItem = (subitem) => isActiveNavPath(subitem.url_path);

    const isActiveMenuItem = (item) => {
        if (isDropStudioTarget(item)) {
            return isMerchPlaygroundOpen;
        }

        const currentLocation = normalizePath(location.pathname);
        const itemPath = normalizePath(resolveMenuUrl(item.url_path));

        if (item.url_path === '/') {
            return currentLocation === '/';
        }
        if (item.item_type === 'dropdown') {
            return item.subitems?.some((subitem) => isActiveSubMenuItem(subitem));
        }
        return isActiveNavPath(item.url_path);
    };

    useEffect(() => {
        if (!headerRef.current) return undefined;

        const updateHeaderHeight = () => {
            document.documentElement.style.setProperty('--site-header-height', `${headerRef.current?.offsetHeight || 117}px`);
        };

        updateHeaderHeight();
        const resizeObserver = new ResizeObserver(updateHeaderHeight);
        resizeObserver.observe(headerRef.current);
        window.addEventListener('resize', updateHeaderHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, []);

    useEffect(() => {
        const handleOpen = () => setIsMerchPlaygroundOpen(true);
        const handleClose = () => setIsMerchPlaygroundOpen(false);

        window.addEventListener(OPEN_MERCH_PLAYGROUND_EVENT, handleOpen);
        window.addEventListener(CLOSE_MERCH_PLAYGROUND_EVENT, handleClose);

        return () => {
            window.removeEventListener(OPEN_MERCH_PLAYGROUND_EVENT, handleOpen);
            window.removeEventListener(CLOSE_MERCH_PLAYGROUND_EVENT, handleClose);
        };
    }, []);

    return (
        <>
            <div ref={headerRef} className="fixed inset-x-0 top-0 z-[70] w-full">
                <div className="bg-black text-white text-center py-2.5 text-xs sm:text-[13px] uppercase border-b border-white/10 merch-font font-normal">
                    Bespoke custom tees and hoodies for creatives, by creatives.
                </div>
                <header className="relative flex flex-wrap md:flex-col md:justify-start md:flex-nowrap w-full bg-[#171717] border-b border-[#2b2b2b]">
                    <nav className="relative w-full mx-auto text-center">
                        <div className="w-full px-4 sm:px-5 site-header-inner">
                            <div className="relative md:flex md:min-h-[84px] md:items-center md:justify-between md:gap-3 py-3 md:py-0">
                                {/* Logo - Always visible */}
                                <div className="flex justify-between items-center gap-x-1">
                                    {/* Desktop Logo */}
                                    <Link
                                        className="absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 flex-none font-semibold text-xl text-white focus:outline-none focus:opacity-80 md:inline-block"
                                        to="/"
                                        onClick={scrollToTop}
                                    >
                                        <img 
                                            src='/img/logo-inverted.png' 
                                            title="ThatShirtShow" 
                                            className="site-header-logo h-auto"
                                        />
                                    </Link>

                                    {/* Mobile Menu Button and Logo */}
                                    <div className="grid w-full grid-cols-[48px_1fr_48px] items-center md:hidden">
                                        <div className="flex justify-start">
                                            <button
                                                type="button"
                                                className="p-2 inline-flex justify-center items-center gap-2 rounded-lg border border-white/20 bg-transparent text-white hover:border-[#aade00] hover:text-[#aade00]"
                                                onClick={toggleMobilePanel}
                                                aria-label="Open menu"
                                            >
                                                <MenuIcon />
                                            </button>
                                        </div>
                                        <Link to="/" className="flex justify-center" onClick={scrollToTop}>
                                            <img 
                                                src='/img/logo-inverted.png'
                                                title="ThatShirtShow" 
                                                className="w-[170px] h-auto"
                                            />
                                        </Link>
                                        <div></div>
                                    </div>
                                </div>

                                {/* Main Navigation and Icons Container */}
                                <div className="hidden h-full flex-1 md:block">
                                    <div className="transition-opacity duration-150 h-full block">
                                        <div className="hs-collapse overflow-visible transition-all duration-300 basis-full grow md:block h-full">
                                            <div className="flex min-h-[84px] items-center justify-between h-full site-header-row">
                                                <nav className="site-desktop-nav hidden flex-nowrap items-center gap-y-3 text-left lg:flex">
                                                    {desktopNavItems.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className="relative group flex items-center overflow-visible"
                                                            onMouseEnter={() => item.item_type === 'dropdown' && setActiveDropdown(item.id)}
                                                            onMouseLeave={() => setActiveDropdown(null)}
                                                        >
                                                            {isDropStudioTarget(item) ? (
                                                                <a
                                                                    href="/#merch-playground"
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        handleDropStudioClick();
                                                                    }}
                                                                    className={`site-desktop-nav-link uppercase flex items-center gap-1 py-2 relative merch-font whitespace-nowrap
                                                                        ${isActiveMenuItem(item)
                                                                            ? 'text-white after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-0.5 after:bg-[#aade00]'
                                                                            : 'text-white hover:text-[#aade00]'
                                                                        }`}
                                                                >
                                                                    {item.name}
                                                                </a>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleMenuItemClick(item)}
                                                                    className={`site-desktop-nav-link uppercase flex items-center gap-1 py-2 relative merch-font whitespace-nowrap
                                                                        ${isActiveMenuItem(item)
                                                                            ? 'text-white after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-0.5 after:bg-[#aade00]'
                                                                            : 'text-white hover:text-[#aade00]'
                                                                        }`}
                                                                >
                                                                    {item.name}
                                                                    {item.item_type === 'dropdown' && (
                                                                        <KeyboardArrowDownIcon className="w-5 h-5" />
                                                                    )}
                                                                </button>
                                                            )}
                                                            
                                                            {item.item_type === 'dropdown' && activeDropdown === item.id && (
                                                                <div 
                                                                    className="absolute left-0 top-full bg-[#151515] border border-[#333] z-[80] min-w-[240px] overflow-visible py-2 text-left shadow-2xl"
                                                                    style={{ marginTop: '1px' }}
                                                                >
                                                                    {item.subitems?.map((subitem) => {
                                                                        const isSubitemActive = isActiveSubMenuItem(subitem);
                                                                        return (
                                                                            <button
                                                                            key={subitem.id}
                                                                            onClick={() => handleMenuItemClick(subitem)}
                                                                            className={`block w-full text-left px-4 py-2.5 text-[18px] font-bold ${
                                                                                isSubitemActive
                                                                                    ? 'text-[#aade00] bg-white/5'
                                                                                    : 'text-white/80 hover:bg-white/5 hover:text-[#aade00]'
                                                                            } cursor-pointer`}
                                                                        >
                                                                            {subitem.name}
                                                                        </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </nav>

                                                {/* Right-side Icons */}
                                                <div className="site-header-actions flex items-center justify-end">
                                                    {/* Account/Auth Section */}
                                                    {isAuthorized ? (
                                                        <button
                                                            onClick={handleAccountClick}
                                                            className="flex items-center justify-center h-12 text-white hover:text-[#aade00]"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <AccountCircleIcon sx={{ fontSize: 25 }} />
                                                                <span className="site-header-action-text uppercase merch-font whitespace-nowrap">My Account</span>
                                                            </div>
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-4">
                                                            <button
                                                                onClick={() => navigateToTop('/login')}
                                                                className="flex items-center gap-2 text-white hover:text-[#aade00]"
                                                            >
                                                                <PersonIcon sx={{ fontSize: 25 }} />
                                                                <span className="site-header-action-text uppercase merch-font whitespace-nowrap">Sign In</span>
                                                            </button>
                                                            <button
                                                                onClick={() => navigateToTop('/register')}
                                                                className="site-register-button flex items-center rounded-lg bg-[#aade00] text-[#111] hover:bg-[#c4ff18] merch-font uppercase whitespace-nowrap"
                                                            >
                                                                <span>Register</span>
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Basket Icon */}
                                                    <a className="flex items-center justify-center w-14 h-14 border-l border-white/10 pl-5 text-white hover:text-[#aade00]" 
                                                       href="/shop/basket/">
                                                        <div className="relative">
                                                            <LocalMallIcon sx={{ fontSize: 29 }} />
                                                            {basketCount > 0 && (
                                                                <div className={`absolute -top-2 -right-2 bg-[#aade00] text-[#111] rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold transform ${isUpdating ? 'animate-bounce' : ''}`}>
                                                                    {basketCount}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </nav>
                </header>
            </div>
            <div style={{ height: 'var(--site-header-height, 117px)' }} aria-hidden="true"></div>
            {/* Mobile Side Panel */}
            {showMobilePanel && (
                <div className="fixed inset-y-0 left-0 w-72 bg-[#111] text-white shadow-2xl z-[90] overflow-y-auto border-r border-white/10">
                    <div className="flex justify-between items-center p-4 border-b border-white/10">
                        <Typography variant="h6" className="font-semibold uppercase merch-font">
                            {t('mobileMenu.menu')}
                        </Typography>
                        <button
                            onClick={toggleMobilePanel}
                            className="text-white/70 hover:text-[#aade00]"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Main Navigation */}
                    <div className="py-2">
                        {navItemsWithStudio.map((item) => {
                            if (item.item_type === 'dropdown' && item.subitems?.length) {
                                return (
                                    <div key={item.id}>
                                        <div className="px-4 py-2 text-xs font-semibold uppercase text-white/45 merch-font">
                                            {item.name}
                                        </div>
                                        {item.subitems.map((subitem) => (
                                            <HeaderLink
                                                key={subitem.id}
                                                link={resolveMenuUrl(subitem.url_path)}
                                                title={subitem.name}
                                                isMobile
                                                isActive={isActiveSubMenuItem(subitem)}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleMenuItemClick(subitem);
                                                }}
                                            />
                                        ))}
                                    </div>
                                );
                            }

                            return (
                                <HeaderLink
                                    key={item.id}
                                    link={resolveMenuUrl(item.url_path)}
                                    title={item.name}
                                    isMobile
                                    isActive={isActiveMenuItem(item)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleMenuItemClick(item);
                                    }}
                                />
                            );
                        })}
                    </div>

                    {/* Common Links (Basket & Wishlist) */}
                    <div className="py-2 border-t border-white/10">
                        <div className="px-4 py-2">
                            <Typography variant="subtitle2" className="text-white/45 font-semibold uppercase">
                                {t('mobileMenu.commonLinks')}
                            </Typography>
                        </div>
                        {navigationLinks.common.map((item) => (
                            <HeaderLink
                                key={item.id}
                                link={item.url}
                                title={t(`header.${item.text.toLowerCase()}`)}
                                icon={item.icon}
                                isMobile
                                isActive={isActiveNavPath(item.url)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleMobileLinkClick(item.url);
                                }}
                            />
                        ))}
                    </div>

                    {/* Account Section */}
                    <div className="py-2 border-t border-white/10">
                        <div className="px-4 py-2">
                            <Typography variant="subtitle2" className="text-white/45 font-semibold uppercase">
                                My Account
                            </Typography>
                        </div>
                        {isAuthorized
                            ? navigationLinks.account.authenticated.map((item) => (
                                <HeaderLink
                                    key={item.id}
                                    link={item.url}
                                    title={item.text}
                                    icon={item.icon}
                                    isMobile
                                    isActive={isActiveNavPath(item.url)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleMobileLinkClick(item.url);
                                    }}
                                />
                            ))
                            : navigationLinks.account.guest.map((item) => (
                                <HeaderLink
                                    key={item.id}
                                    link={item.url}
                                    title={item.text}
                                    icon={item.icon}
                                    isMobile
                                    isActive={isActiveNavPath(item.url)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleMobileLinkClick(item.url);
                                    }}
                                />
                            ))
                        }
                    </div>

                    {/* Help & Support */}
                    <div className="py-2 border-t border-white/10">
                        <div className="px-4 py-2">
                            <Typography variant="subtitle2" className="text-white/45 font-semibold uppercase">
                                Help & Support
                            </Typography>
                        </div>
                        {navigationLinks.help.map((item) => (
                            <HeaderLink
                                key={item.id}
                                link={item.url}
                                title={item.text}
                                icon={item.icon}
                                isMobile
                                isActive={isActiveNavPath(item.url)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleMobileLinkClick(item.url);
                                }}
                            />
                        ))}
                    </div>

                    {/* More Links */}
                    <div className="py-2 border-t border-white/10">
                        <div className="px-4 py-2">
                            <Typography variant="subtitle2" className="text-white/45 font-semibold uppercase">
                                More
                            </Typography>
                        </div>
                        {navigationLinks.more.map((item) => (
                            <HeaderLink
                                key={item.id}
                                link={item.url}
                                title={item.text}
                                icon={item.icon}
                                isMobile
                                isActive={isActiveNavPath(item.url)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleMobileLinkClick(item.url);
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
            {/* Overlay - updated z-index */}
            {showMobilePanel && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-[80]" 
                    onClick={toggleMobilePanel}
                ></div>
            )}
            {/* Mobile Sticky Footer Navigation */}
            <Box 
                className="fixed bottom-0 left-0 right-0 bg-[#111] border-t border-white/10 py-2 px-4 md:hidden z-50"
                sx={{
                    boxShadow: '0px -4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
            >
                <div className="flex justify-around items-center">
                    <Link
                        to={isAuthorized ? "/account/profile" : "/login"}
                        className={`flex flex-col items-center hover:text-[#aade00] py-1 
                            ${isActivePath('/account') || isActivePath('/login') 
                                ? 'text-[#aade00]' 
                                : 'text-white/70'}`}
                        onClick={scrollToTop}
                    >
                        <AccountCircleIcon sx={{ fontSize: 28 }} />
                        <span className="text-xs mt-1">{isAuthorized ? 'Profile' : 'Login'}</span>
                    </Link>

                    <Link
                        to="/shop/basket"
                        className={`flex flex-col items-center hover:text-[#aade00] py-1 
                            ${isActivePath('/shop/basket') 
                                ? 'text-[#aade00]' 
                                : 'text-white/70'}`}
                        onClick={scrollToTop}
                    >
                        <div className="relative">
                            <LocalMallIcon sx={{ fontSize: 28 }} />
                            {basketCount > 0 && (
                                <div className={`absolute -top-2 -right-2 bg-[#aade00] text-[#111] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold transform ${isUpdating ? 'animate-bounce' : ''}`}>
                                    {basketCount}
                                </div>
                            )}
                        </div>
                        <span className="text-xs mt-1">Basket</span>
                    </Link>
                </div>
            </Box>
        </>
    );
}
